import $ from "jquery";
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { callGetApi, callPostApi } from "../../api/ApiCaller";
import { GET_GAME_PLAY_RESULT_POST, GET_PLAYER_BALANCE_GET, GET_TEMPLATE_DETAILS_GET, RESET_BALANCE_AMT_POST, ADD_BALANCE_AMT_POST } from "../../api/ApiConst";
import { CURRENCY_CODE, getDataFromStore, TEMPLATE_ID } from "../../utils/CookiesUtils";
import { getFormattedAmountWithDenomination, isEmpty } from "../../utils/TextUtils";
import JackpotGameBonus from "../tv-millionario/JackpotGameBonus";
import YouWin, { preloadYouWin, youWinPaidAt, YOU_WIN_MS } from "../tv-millionario/YouWin";
import DoubleUp, { preloadDoubleUp } from "../tv-millionario/DoubleUp";
import LuckyWheelBonus, { preloadLuckyWheel } from "../tv-millionario/LuckyWheelBonus";
import { TV_SND, playTv, chainTv, stopTv, preloadTvSounds } from "../tv-millionario/tvSound";
import CoinContainer from "../CoinContainer";
import "../tv-millionario/TvMillionario.css";

// TV Millionario: the Halloween_25 game (same template, same API) dressed as
// the TV Millionario machine in TvMillionarioGame.MOV. Whatever bonus the
// server deals plays as the machine's own JACKPOT GAME (JackpotGameBonus),
// paying the amount the server returns for it.
// Everything visual is placed on the machine's own 800x600 stage.

const TV = `${process.env.PUBLIC_URL}/tv-milionario`;
const SFX = `${process.env.PUBLIC_URL}/assets`;

// DEV ONLY: open the JACKPOT GAME bonus (test payout, nothing paid) as soon
// as the game loads. Set to false to start on the reels as normal.
// const OPEN_BONUS_ON_LOAD = true;
const OPEN_BONUS_ON_LOAD = false;
// DEV ONLY: show the YOU WIN screen (41000, shown only, nothing paid) once as
// soon as the game loads. Set to false to start on the reels as normal.
const OPEN_YOU_WIN_ON_LOAD = false;
// ...and which one: "girl" (the hostess, over the reels) or "guy" (the host)
// const YOU_WIN_ON_LOAD_VARIANT = "guy";
const YOU_WIN_ON_LOAD_VARIANT = "guy";
// DEV ONLY: open the DOBLAR card screen (a 2400 win, shown only, nothing
// paid) as soon as the game loads. Set to false to start on the reels.
const OPEN_DOUBLE_ON_LOAD = false;
// DEV ONLY: open the LUCKY WHEEL bonus (a 60000 pot, shown only, nothing
// paid) as soon as the game loads. Set to false to start on the reels.
const OPEN_WHEEL_ON_LOAD = false;
// Which TV bonus each server bonus plays as: the server's wheels (roulette,
// gira luz) are the LUCKY WHEEL, everything else the JACKPOT GAME.
const WHEEL_BONUS_TYPES = ["Bonus02Roulette", "Bonus07GiraLuz"];
// YOU WIN (see runYouWin): shown for a win of at least this many total bets
// (the video's line win was 22 bets; its small wins never show it), and
// after every bonus.
const YOU_WIN_MIN_BETS = 10;
// DOBLAR (DoubleUp.jsx): after a line win the reels offer TOMAR O DOBLAR.
// The card is drawn HERE, in the browser — the server has no double-up
// call, so it never hears about a doubled or lost win and the real balance
// would not follow. Dev only until the backend has one.
const DOUBLE_UP_ENABLED = process.env.NODE_ENV === "development";

const STAGE_W = 800;
const STAGE_H = 600;
const REEL_X = 60;
const REEL_Y = 109;
const CELL_W = 136;
const CELL_H = 364 / 3;

// The backend speaks Halloween symbol names. Each one gets the TV symbol of
// about the same rank on the TV pay table (fig_NN, 1-based).
const FIG_BY_TITLE = {
    abobora: 12,      // top symbol / accumulado     -> JACKPOT wheel
    diabo: 10,        // 2000                        -> host
    magica: 9,        // 500                         -> hostess
    bruxinha: 11,     // bonus witch                 -> FREE GAME
    calderao: 7,      // bonus cauldron              -> ship
    espantalho: 8,    // 200                         -> plane
    bruxa: 6,         // 150                         -> house
    vampiro: 5,       // 75                          -> car
    frankenstein: 4,  // 30                          -> ring
    fantasma: 3,      // 30                          -> money
};
// frames in slot/fig_NN (the win animation of each symbol)
const FIG_FRAMES = [60, 55, 45, 46, 53, 52, 60, 60, 61, 59, 59, 59];
// The spinning reel. slot/slotsfast 00..29 are 30 frames of one blurred reel
// strip scrolling DOWN 47px a frame and looping (30..59 is the same strip
// darkened, which the video never shows). Those frames were stitched back
// into the one strip they were cut from, slotsfast/strip.png (132 x 1410),
// so each reel scrolls it continuously and its three cells join up exactly
// the way the machine's do — whole frames could not, since a cell is 118px
// = 2.5 frames of travel.
const STRIP = `${TV}/slot/slotsfast/strip.png`;
const STRIP_H = 1410;
const STRIP_STEP = 47; // px per 33ms tick, as in the recording

// The header logo: main_logo 00..59 are opaque 298x60 crops of the
// "MILLONARIO" area of MAINT, cut at (257, 20) (matched pixel for pixel),
// so they sit straight over the static logo. The frames are one white glint
// running left to right (00-27) and back (28-59). In TvMillionarioGame.MOV
// it sits at the left end at ~0s and ~10.35s and reaches the right at ~14.8s,
// so the loop is ~10.4s: 170ms a frame.
const LOGO_FRAMES = 60;
const LOGO_FRAME_MS = 170;
const logoFrame = (f) => `${TV}/main_logo/sprite_${pad2(f)}.png`;

const MainLogo = () => {
    const [frame, setFrame] = useState(0);
    useEffect(() => {
        for (let f = 0; f < LOGO_FRAMES; f++) { const im = new Image(); im.src = logoFrame(f); }
        const id = setInterval(() => setFrame((f) => (f + 1) % LOGO_FRAMES), LOGO_FRAME_MS);
        return () => clearInterval(id);
    }, []);
    return <img className="tvm-abs" alt="" src={logoFrame(frame)} style={{ left: 257, top: 20, width: 298, height: 60 }} />;
};

// Order of the line badges down each side, as on the machine.
const LEFT_BADGES = [10, 4, 2, 8, 12, 6, 1, 7, 13, 9, 3, 5, 11];
const RIGHT_BADGES = [22, 16, 14, 20, 24, 18, 19, 25, 21, 15, 17, 23];
const BADGE_W = 37;
// The L??*.png badges are not one size: each is 37 wide but 34..56 tall, and
// the art sits at a different height inside each file. [file height, y of the
// art's visual centre in the file], measured off the alpha. Drawn at natural
// size and placed by that centre.
// Star files (ON = line in play, W = winning line):
const BADGE_ART = {
    1: [41, 22.5], 2: [49, 26.5], 3: [40, 20], 4: [49, 26], 5: [44, 22], 6: [49, 26], 7: [37, 20.5],
    8: [49, 26.5], 9: [38, 18.5], 10: [49, 26.5], 11: [47, 22.5], 12: [49, 29.5], 13: [34, 17],
    14: [44, 23], 15: [55, 35.5], 16: [47, 25], 17: [56, 34], 18: [45, 29.5], 19: [46, 28.5],
    20: [41, 21.5], 21: [55, 37.5], 22: [50, 27.5], 23: [54, 32], 24: [46, 30], 25: [44, 27],
};
// Dim purple tile files (D = line not in play):
const BADGE_ART_D = {
    1: [37, 22], 2: [47, 25.5], 3: [40, 20], 4: [48, 24.5], 5: [43, 21.5], 6: [41, 24], 7: [36, 21],
    8: [45, 25.5], 9: [38, 19.5], 10: [49, 24.5], 11: [46, 23], 12: [46, 29], 13: [34, 18],
    14: [44, 22.5], 15: [55, 35], 16: [47, 23.5], 17: [56, 34], 18: [44, 29], 19: [45, 30],
    20: [41, 20.5], 21: [55, 37], 22: [50, 25], 23: [54, 31.5], 24: [46, 28.5], 25: [44, 28],
};
const LEFT_BADGE_X = 4;
const RIGHT_BADGE_X = 759;
// Stage y of each badge's centre, read off a screenshot of the real machine
// (937x591, i.e. the 800x600 stage at 1.171 x 0.985). They are NOT evenly
// spaced: each badge sits where its own line leaves the reels, which is why
// e.g. 20 -> 24 is a bigger step than 24 -> 18.
const BADGE_CY = {
    // left, top to bottom
    10: 104, 4: 134, 2: 166, 8: 198, 12: 229, 6: 260, 1: 289,
    7: 318, 13: 347, 9: 378, 3: 410, 5: 442, 11: 474,
    // right, top to bottom
    22: 104, 16: 135, 14: 168, 20: 201, 24: 242, 18: 274,
    19: 305, 25: 337, 21: 376, 15: 409, 17: 442, 23: 472,
};
const badgePos = (n, state = "ON") => {
    const left = LEFT_BADGES.includes(n);
    const cy = BADGE_CY[n];
    const [h, artCy] = (state === "D" ? BADGE_ART_D : BADGE_ART)[n];
    return {
        left,
        x: left ? LEFT_BADGE_X : RIGHT_BADGE_X,
        top: cy - artCy,
        h,
        cy,
    };
};
// SELECCIONAR LINEAS steps through these: lines 1-3, then 1-5, 1-10, 1-15,
// and all 25. lines/refactored/sprite_N is the machine's own picture of
// exactly those lines (counted off the files: 3, 5, 10, 15, 25 line ends),
// placed at (42,106) on the stage.
const LINE_OPTIONS = [3, 5, 10, 15, 25];
const LINE_SPRITE = { 3: 0, 5: 1, 10: 2, 15: 3, 25: 4 };

const pad2 = (n) => String(n).padStart(2, "0");
const slotImg = (fig, variant = 0) => `${TV}/slot/slots/sprite_${pad2(fig - 1 + variant * 12)}.png`;
const figFrame = (fig, frame) => `${TV}/slot/fig_${pad2(fig)}/sprite_${pad2(frame % FIG_FRAMES[fig - 1])}.png`;
const figOf = (cell) => FIG_BY_TITLE[cell?.title] || 1;

const randomGrid = () => {
    const figs = Object.values(FIG_BY_TITLE).concat([1, 2]);
    return [0, 1, 2].map(() => [0, 1, 2, 3, 4].map(() => figs[Math.floor(Math.random() * figs.length)]));
};

// Digits drawn from the machine's own bitmap fonts (fnt_*.png, 10 glyphs).
const FONTS = {
    blue: { src: `${TV}/footer-buttons/fnt_blue.png`, w: 14, h: 18, sheet: 140 },
    red: { src: `${TV}/footer-buttons/fnt_red.png`, w: 14, h: 18, sheet: 140 },
    white: { src: `${TV}/footer-buttons/fnt_white.png`, w: 16, h: 18, sheet: 160 },
};

const Digits = ({ value, font = "blue", scale = 1 }) => {
    const f = FONTS[font];
    const text = String(Math.max(0, Math.floor(Number(value) || 0)));
    return (
        <span style={{ display: "inline-flex" }}>
            {text.split("").map((d, i) => (
                <span key={i} style={{
                    width: f.w * scale,
                    height: f.h * scale,
                    backgroundImage: `url(${f.src})`,
                    backgroundSize: `${f.sheet * scale}px ${f.h * scale}px`,
                    backgroundPosition: `${-Number(d) * f.w * scale}px 0`,
                }} />
            ))}
        </span>
    );
};

// A footer button from footer-buttons/refactored/<name>/sprite_{0 off,1 on,2 lit}.
export const TvButton = ({ name, x, disabled, lit, onClick, children, style }) => {
    const [pressed, setPressed] = useState(false);
    const state = disabled ? 0 : (pressed || lit ? 2 : 1);
    const src = name.startsWith("bt_") && !["bt_menu", "bt_none", "bt_skip"].includes(name)
        ? `${TV}/footer-buttons/refactored/${name}/sprite_${state}.png`
        : `${TV}/footer-buttons/${name}.png`;
    return (
        <button className="tvm-btn" disabled={disabled}
            style={{ left: x, backgroundImage: `url(${src})`, ...style }}
            onPointerDown={() => setPressed(true)}
            onPointerUp={() => setPressed(false)}
            onPointerLeave={() => setPressed(false)}
            onClick={onClick}>
            {children}
        </button>
    );
};

// When each reel stops, in ms from the INICIAR press: one after another,
// left to right, 1/3s apart — the gap between reels 1 and 2 in
// TvMillionarioGame.MOV. Every reel blurs at once and each snaps straight
// onto its symbols (no bounce).
const REEL_STOP_MS = [333, 667, 1000, 1333, 1667];
const SETTLE_MS = 100; // after the last reel, before the result is shown
const LINE_PULSE_MS = 530; // one linhapremiada per winning line, this far apart

const HELP_PAGES = [1, 2, 3, 4, 5, 6].map((n) => `${TV}/help/TABELA0${n}.png`);

// Marquee bulbs round the reels. Centres found off the frame art (MAINT's
// bulb row at y 102, MAINE/MAIND's columns at x 52 / 748, MAINB2's row at
// y 481), 17.4px apart. In the video a lit band about half a side long runs
// clockwise along each side — top left->right, right down, bottom
// right->left, left up — and comes round every ~2s; the sides run a little
// out of step with the top and bottom. `delay` puts each bulb on that wave.
const CHASE_PERIOD = 2; // s
const BULBS = (() => {
    const list = [];
    const side = (count, at, phase) => {
        for (let k = 0; k < count; k++) {
            const f = k / (count - 1);
            const { x, y } = at(f);
            // one side takes half a period to sweep
            list.push({ x, y, delay: -CHASE_PERIOD * (1 - ((f * 0.5 + phase) % 1)) });
        }
    };
    side(41, (f) => ({ x: 51.5 + f * 697, y: 102 }), 0);      // top
    side(21, (f) => ({ x: 748, y: 115 + f * 348 }), 0.4);     // right
    side(41, (f) => ({ x: 748.5 - f * 697, y: 481 }), 0);     // bottom
    side(21, (f) => ({ x: 52, y: 463 - f * 348 }), 0.4);      // left
    return list;
})();

const TvMillionarioGame = () => {
    const navigate = useNavigate();
    const templateId = getDataFromStore(TEMPLATE_ID);
    const currency = getDataFromStore(CURRENCY_CODE);

    const [templateData, setTemplateData] = useState(null);
    const [walletBalance, setWalletBalance] = useState(0);
    const [gameResult, setGameResult] = useState(null);
    const [winnings, setWinnings] = useState(0);
    // starts on the fewest lines, as the ice game does
    const [selectedNumber, setSelectedNumber] = useState(LINE_OPTIONS[0]);
    // after a spin the next SELECCIONAR LINEAS press starts over from the fewest
    const [linesFromStart, setLinesFromStart] = useState(false);
    const [betMultiplier, setBetMultiplier] = useState(null);
    const [denomination] = useState(0.01);
    const [accumuladoList, setAccumuladoList] = useState([]);
    const [accumuladoWin, setAccumuladoWin] = useState({ winAmount: 0, winIndex: -1 });
    const [keyboardInput, setKeyboardInput] = useState(false);

    const [slotRunning, setSlotRunning] = useState(false);
    const [animateColumn, setAnimateColumn] = useState([false, false, false, false, false]);
    const [buttonFlag, setButtonFlag] = useState(false);       // blocks AYUDA / lines / bet / auto
    const [jogarButtonFlag, setJogarButtonFlag] = useState(false); // blocks INICIAR
    const [autoBetOn, setAutoBetOn] = useState(false);
    const [autoCount, setAutoCount] = useState(0);
    const [playMachine, setPlayMachine] = useState(0);
    const [autoBetTimeOutRef, setAutoBetTimeOutRef] = useState(null);
    const [animationTimeOutRef, setAnimationTimeOutRef] = useState([]);
    const [animationIntervalRef, setAnimationIntervalRef] = useState([]);

    const [showBonusModel, setShowBonusModel] = useState(false);
    const [showCoins, setShowCoins] = useState(false);
    const [showInsufficient, setShowInsufficient] = useState(false);
    const [resetAmntModel, setResetAmntModel] = useState(false);
    const [helpPage, setHelpPage] = useState(-1);
    const [youWin, setYouWin] = useState(null); // the amount on the YOU WIN screen, null = not shown
    // a line win waiting on TOMAR or DOBLAR: { balance, amount }; doubling = the card screen is up
    const [pendingWin, setPendingWin] = useState(null);
    const [doubling, setDoubling] = useState(false);

    // what the reels and lines show
    const [idleGrid] = useState(randomGrid);
    const [winCells, setWinCells] = useState([]);       // [{x:row, y:col}]
    const [winLines, setWinLines] = useState([]);       // numbers of the winning lines
    const [linePreview, setLinePreview] = useState(false);
    const [message, setMessage] = useState("press_init");
    const [tick, setTick] = useState(0);
    // stretched to the whole window, no black bars — same as the ice game.
    // Worked out before the first paint, or the stage flashes up at its
    // native 800x600 and then jumps to full size.
    const fitToWindow = () => ({ x: window.innerWidth / STAGE_W, y: window.innerHeight / STAGE_H });
    const [scale, setScale] = useState(fitToWindow);

    // ---- stage scaling ----
    useLayoutEffect(() => {
        const fit = () => setScale(fitToWindow());
        fit();
        window.addEventListener("resize", fit);
        return () => window.removeEventListener("resize", fit);
    }, []);

    // one clock drives the reel blur (a new blur frame every video frame, as
    // in the recording) and the win animations (fig_NN at 20fps)
    const winning = winCells.length > 0;
    useEffect(() => {
        if (!slotRunning && !winning) return;
        const id = setInterval(() => setTick((t) => t + 1), slotRunning ? 33 : 50);
        return () => clearInterval(id);
    }, [slotRunning, winning]);

    // preload what a spin needs so the first one doesn't flicker
    useEffect(() => {
        const srcs = [];
        srcs.push(STRIP);
        preloadYouWin();
        preloadDoubleUp();
        preloadLuckyWheel();
        preloadTvSounds();
        for (let i = 1; i <= 12; i++) {
            srcs.push(slotImg(i), slotImg(i, 1));
            for (let f = 0; f < FIG_FRAMES[i - 1]; f++) srcs.push(figFrame(i, f));
        }
        srcs.forEach((s) => { const im = new Image(); im.src = s; });
    }, []);

    useEffect(() => {
        getWalletBalance();
        getTemplateData();
    }, [templateId]);

    useEffect(() => {
        if (autoCount > 0 || autoCount === -1) {
            placeBet();
        } else {
            setAutoBetOn(false);
            setAutoCount(0);
        }
    }, [playMachine]);

    const getWalletBalance = () => {
        callGetApi(GET_PLAYER_BALANCE_GET, {}, (response) => {
            if (response.errorCode === 0) {
                setWalletBalance(Math.round(response.responsePacket * 100.0) / 100.0);
            }
        });
    };

    const getTemplateData = () => {
        if (!templateId) { return; }
        callGetApi(GET_TEMPLATE_DETAILS_GET.replaceAll("{recordId}", templateId), {}, (response) => {
            if (response.errorCode === 0) {
                const res = response.responsePacket;
                setKeyboardInput(res.keyboardInput);
                setAccumuladoList(res?.accumuladoList || []);
                setTemplateData(res);
                setBetMultiplier(res.betAmountList[0]);
            } else {
                toast.error(response.message || "Game data not loaded");
            }
        }, (error) => {
            toast.error(typeof error === "string" ? error : "Game data not loaded");
        });
    };

    const playSoundById = (soundTagId) => {
        const el = $("#" + soundTagId)[0];
        if (el) el.play().catch(() => { });
    };

    const linePulses = useRef([]);
    const clearResultDisplay = () => {
        linePulses.current.forEach(clearTimeout);
        linePulses.current = [];
        setWinCells([]);
        setWinLines([]);
    };

    // As in the video: once lines or bet are touched, the chosen lines stay
    // drawn over the reels until the next spin.
    const showLinePreview = () => setLinePreview(true);

    const drawWinningLineOnResult = (pGameResult) => {
        if (!pGameResult || !Array.isArray(pGameResult.payLines) || !pGameResult.winnings) {
            if (pGameResult) setMessage("pierde");
            return;
        }
        setJogarButtonFlag(true);
        if (pGameResult.winnings > 0) {
            setWinnings(pGameResult.winnings);
        }
        // the machine sounds one pulse per winning line, 0.53s apart (8 lines
        // -> 8 pulses at 70.1-73.8s in 3VRlTIPI0To)
        linePulses.current.forEach(clearTimeout);
        linePulses.current = pGameResult.payLines.slice(0, 25).map((_, i) =>
            setTimeout(() => playTv(TV_SND.winLine), i * LINE_PULSE_MS));
        // The machine draws no line on a win: the winning symbols animate,
        // the rest go dark, and the winning line's badge blinks.
        const cells = [];
        const lines = [];
        pGameResult.payLines.forEach((pl) => {
            const num = Number(String(pl.title || "").replace("line-show", "")) || 0;
            (pl.line || []).forEach((c) => {
                if (c.isValidToDrawLine) cells.push({ x: c.x, y: c.y });
            });
            if (num > 0) lines.push(num);
        });
        setWinCells(cells);
        hostessWin.current = cells.some((c) => pGameResult.resultMatrix?.[c.x]?.[c.y]?.title === "magica");
        setWinLines(lines);
        setMessage("win");
    };

    // On the machine the JACKPOT GAME's music starts the moment the three
    // JACKPOTs land, 3.6s before the panels close (qzZSNKlRcCg 51.9s); its bed
    // then runs on under the board (JackpotGameBonus stops it). The LUCKY
    // WHEEL's starts with its own title, so LuckyWheelBonus plays that.
    const startBonusSound = (bonusType) => {
        if (!WHEEL_BONUS_TYPES.includes(bonusType)) chainTv(TV_SND.jackpotIntro, TV_SND.jackpotLoop);
    };

    // JUEGO DE JACKPOT shows over the reels for 3s before the bonus takes
    // the stage (53.25s -> 56.30s in the reference video)
    const openBonusModel = () => {
        setTimeout(() => setShowBonusModel(true), 3000);
    };

    const placeBet = () => {
        if (jogarButtonFlag) { return; }
        if (!betMultiplier) {
            // the template (and with it the bet list) never arrived: say so
            // and ask again, rather than spin on a NaN bet
            toast.error(templateId ? "Game data not loaded, please try again" : "No game selected");
            getTemplateData();
            getWalletBalance();
            return;
        }
        setLinePreview(false);
        clearResultDisplay();
        const totalBetAmount = betMultiplier * selectedNumber;
        if (walletBalance < totalBetAmount) {
            playSoundById("ErrorMp3");
            setShowInsufficient(true);
            setMessage("insert");
            setTimeout(() => setShowInsufficient(false), 3000);
            setAutoBetOn(false);
            setAutoCount(0);
            return;
        }
        if (autoBetTimeOutRef !== null) {
            clearTimeout(autoBetTimeOutRef);
        }
        // from the top every spin: the last one was paused mid-sound at the last stop
        const spinSound = $("#SlotMp3")[0];
        if (spinSound) spinSound.currentTime = 0;
        playSoundById("SlotMp3");
        setLinesFromStart(true);
        setMessage("good_lucky");
        setButtonFlag(true);
        setJogarButtonFlag(true);
        setSlotRunning(true);
        setAnimateColumn([true, true, true, true, true]);

        const payload = {
            slotLineType: `Line_${selectedNumber}`,
            betAmount: getFormattedAmountWithDenomination(totalBetAmount, denomination),
        };
        const lastBalance = walletBalance;
        // what the counter must land on: the balance after this bet, plus the win
        const afterBet = Math.round((lastBalance - totalBetAmount) * 100) / 100.0;
        setWalletBalance(afterBet);
        const pAnimationTimeOutRef = [];
        const spinStartedAt = Date.now();
        // a reel never stops before the result is in: if the server is slow the
        // stops slide back, keeping their spacing
        const sinceStart = (ms) => Math.max(0, ms - (Date.now() - spinStartedAt));

        callPostApi(GET_GAME_PLAY_RESULT_POST.replaceAll("{templateId}", templateId), payload, {}, (response) => {
            setJogarButtonFlag(false);
            if (response.errorCode === 0) {
                const result = response.responsePacket;
                const updatedAccumulado = result?.accumuladoList;
                const accumuladoWinningAmt = result?.accumuladoActual;
                setKeyboardInput(result.keyboardInput);
                setGameResult(result);

                let winningIndex = -1;
                if (Array.isArray(updatedAccumulado) && updatedAccumulado.length > 0) {
                    winningIndex = updatedAccumulado.findIndex((item) => item.accumuladoActual === 0);
                    if (winningIndex !== -1 && accumuladoWinningAmt > 0) {
                        updatedAccumulado[winningIndex].accumuladoActual = accumuladoWinningAmt;
                        setAccumuladoWin({ winAmount: accumuladoWinningAmt, winIndex: winningIndex });
                    } else {
                        setAccumuladoWin({ winAmount: 0, winIndex: -1 });
                    }
                    setAccumuladoList(updatedAccumulado);
                }

                const lag = Math.max(0, Date.now() - spinStartedAt - REEL_STOP_MS[0]);
                const at = (ms) => sinceStart(ms + lag);
                REEL_STOP_MS.forEach((ms, col) => {
                    pAnimationTimeOutRef.push(setTimeout(() => {
                        setAnimateColumn((pre) => ({ ...pre, [col]: false }));
                    }, at(ms)));
                });
                const lastStop = REEL_STOP_MS[REEL_STOP_MS.length - 1];
                // the roll is left to run out on its own: on the machine it ends
                // just after the last reel lands, it isn't cut at the stop
                pAnimationTimeOutRef.push(setTimeout(() => {
                    drawWinningLineOnResult(result);
                    if (result.bonusType === "None") {
                        if (accumuladoWinningAmt > 0) {
                            // an accumulado with no line win: keep the keys
                            // locked through the 25s of coins, or a new spin
                            // starts and the late payout overwrites its balance
                            setJogarButtonFlag(true);
                            setMessage("win");
                            setShowCoins(true);
                            setTimeout(() => {
                                setShowCoins(false);
                                animateAccumuladoWinning(accumuladoWinningAmt, winningIndex);
                                playWinningAnimation(afterBet, result.winnings, accumuladoWinningAmt);
                            }, 25000);
                        } else {
                            settleLineWin(afterBet, result.winnings);
                        }
                    } else {
                        setMessage("juego_jack");
                        startBonusSound(result.bonusType);
                        openBonusModel();
                    }
                }, at(lastStop + SETTLE_MS)));

                pAnimationTimeOutRef.push(setTimeout(() => {
                    setSlotRunning(false);
                    setButtonFlag(result.winnings > 0 || accumuladoWinningAmt > 0);
                }, at(lastStop + SETTLE_MS)));

                // next auto spin, counted from the last reel stopping: 1.5s on a
                // loss, 4s for the win to count up, 35s for an accumulado
                let autoTimer = result?.winnings <= 0 ? 1500 : 4000;
                if (accumuladoWinningAmt > 0) autoTimer = 35000;
                autoTimer += at(lastStop + SETTLE_MS);
                if (autoBetOn && result.bonusType === "None") {
                    const pAutoBetTimeOutRef = setTimeout(() => {
                        setAutoCount((prev) => (prev > 0 ? prev - 1 : prev === 0 ? 0 : -1));
                        setPlayMachine((p) => p + 1);
                    }, autoTimer);
                    setAutoBetTimeOutRef(pAutoBetTimeOutRef);
                    pAnimationTimeOutRef.push(pAutoBetTimeOutRef);
                }
                setAnimationTimeOutRef(pAnimationTimeOutRef);
            } else if (response.errorCode === 1001) {
                toast.error(response.message);
                window.location.reload();
            } else {
                spinFailed(lastBalance, response.message);
            }
        }, (error) => {
            spinFailed(lastBalance, error);
        });
    };

    const spinFailed = (lastBalance, msg) => {
        setButtonFlag(false);
        setJogarButtonFlag(false);
        setSlotRunning(false);
        toast.error(typeof msg === "string" ? msg : "Error");
        setWalletBalance(Math.round(lastBalance * 100) / 100.0);
        setAnimateColumn([false, false, false, false, false]);
        setMessage("press_init");
        $("#SlotMp3")[0]?.pause();
    };

    // INICIAR / QUICK while the reels turn: stop everything and settle now
    const stopAllAnimation = () => {
        if (!gameResult) return;
        const slot = $("#SlotMp3")[0];
        if (slot) { slot.pause(); slot.currentTime = 0; }
        setAnimateColumn([false, false, false, false, false]);
        setSlotRunning(false);
        animationTimeOutRef.forEach((r) => clearTimeout(r));
        animationIntervalRef.forEach((r) => clearInterval(r));
        drawWinningLineOnResult(gameResult);

        setWinnings(0);

        const lastBalance = walletBalance;
        if (gameResult.winnings > 0 || accumuladoWin.winAmount > 0) {
            setButtonFlag(true);
            setWinnings(gameResult.winnings);
            if (gameResult.bonusType === "None") {
                if (accumuladoWin.winAmount > 0) {
                    setJogarButtonFlag(true);
                    setMessage("win");
                    setShowCoins(true);
                    setTimeout(() => {
                        setShowCoins(false);
                        animateAccumuladoWinning(accumuladoWin.winAmount, accumuladoWin.winIndex);
                        playWinningAnimation(lastBalance, gameResult.winnings, accumuladoWin.winAmount);
                    }, 25000);
                } else {
                    settleLineWin(lastBalance, gameResult.winnings);
                }
            }
        }
        if (gameResult.bonusType !== "None") {
            setMessage("juego_jack");
            startBonusSound(gameResult.bonusType);
            openBonusModel();
        } else if (!(gameResult.winnings > 0 || accumuladoWin.winAmount > 0)) {
            setButtonFlag(false);
        }
    };

    const animateAccumuladoWinning = (amount, accIndex) => {
        setShowCoins(false);
        if (!amount || amount <= 0) return;
        let timeFactor = Math.min(Math.max(5000 / amount, 2), 20);
        if (timeFactor * amount < 2000) timeFactor = Math.ceil(2000 / amount);
        const decrementFactor = Math.max(1, Math.ceil((amount * timeFactor) / 500));
        let current = amount;
        setTimeout(() => {
            const intervalId = setInterval(() => {
                if (current > 0) {
                    current -= decrementFactor;
                    setAccumuladoList((prevList) => {
                        if (!Array.isArray(prevList)) return prevList;
                        const updated = [...prevList];
                        if (updated[accIndex]) {
                            updated[accIndex] = { ...updated[accIndex], accumuladoActual: Math.max(0, current) };
                        }
                        return updated;
                    });
                } else {
                    clearInterval(intervalId);
                }
            }, timeFactor);
        }, 1600);
    };

    // YOU WIN: the host screen over the reels while GANA pays into CRÉDITO,
    // at the pace in YouWin.jsx; at YOU_WIN_MS it cuts back to the reels and
    // the rest lands at once. A press (TOMAR / the screen / "b") cuts it
    // early, as the player does at 0:34 in the video. onStep(left, step)
    // after each step, onDone() at the end (it must settle the balance).
    const youWinSkip = useRef(false);
    // which YOU WIN: the hostess when she is in the winning line (as in the
    // machine screenshot, a win on her symbols), otherwise the host — the
    // only one the videos show, after line wins and bonuses alike. A guess
    // at the machine's rule; set by drawWinningLineOnResult.
    const hostessWin = useRef(false);
    const [youWinVariant, setYouWinVariant] = useState("guy");
    const runYouWin = (amount, onStep, onDone, variant = "guy") => {
        setYouWinVariant(variant);
        youWinSkip.current = false;
        setYouWin(amount);
        setMessage("take");
        const startedAt = Date.now();
        let paid = 0;
        const interval = setInterval(() => {
            const t = Date.now() - startedAt;
            if (t >= YOU_WIN_MS || youWinSkip.current) {
                clearInterval(interval);
                setYouWin(null);
                onDone();
                return;
            }
            const target = youWinPaidAt(amount, t);
            if (target > paid) {
                const step = target - paid;
                paid = target;
                setYouWin(amount - paid);
                onStep(amount - paid, step);
            }
        }, 33);
        setAnimationIntervalRef([interval]);
    };
    const skipYouWin = () => { youWinSkip.current = true; };

    // YOU WIN: the machine's payout music, the same under the host and the
    // hostess (3VRl 206.8s, qzZS 33.0s/114.5s, 9RTb 22.8s/201.6s), cut when
    // the screen goes. The accumulado's coins keep PREMIO_ALTO (no video of one).
    const youWinOn = youWin !== null;
    useEffect(() => {
        if (!youWinOn) return;
        playTv(TV_SND.youWin);
        return () => stopTv(TV_SND.youWin);
    }, [youWinOn]);
    useEffect(() => {
        if (!showCoins) return;
        chainTv(TV_SND.bigWinStart, TV_SND.bigWin);
        return () => stopTv(TV_SND.bigWinStart, TV_SND.bigWin);
    }, [showCoins]);

    // A plain line win: paid at once, or (DOBLAR) held on TOMAR O DOBLAR.
    // Auto play always takes.
    const settleLineWin = (balance, amount) => {
        if (!DOUBLE_UP_ENABLED || autoBetOn || !(amount > 0)) {
            playWinningAnimation(balance, amount, 0);
            return;
        }
        setButtonFlag(true);
        setJogarButtonFlag(true);
        setWinnings(amount);
        setPendingWin({ balance, amount });
    };
    const takeWin = () => {
        if (!pendingWin || doubling) return;
        playSoundById("ButtonClickMp3");
        setPendingWin(null);
        if (pendingWin.test) { endTestWin(); return; }
        playWinningAnimation(pendingWin.balance, pendingWin.amount, 0);
    };
    // DEV ONLY: a made-up win on the DOBLAR card screen (OPEN_DOUBLE_ON_LOAD,
    // or the "o" key). Whatever happens, CRÉDITO is left alone.
    const showTestDouble = () => {
        setButtonFlag(true);
        setJogarButtonFlag(true);
        setWinnings(2400);
        setPendingWin({ balance: walletBalance, amount: 2400, test: true });
        setDoubling(true);
    };
    const endTestWin = () => {
        setWinnings(0);
        setButtonFlag(false);
        setJogarButtonFlag(false);
        setMessage("press_init");
    };
    const startDouble = () => {
        if (!pendingWin || doubling) return;
        playSoundById("ButtonClickMp3");
        setDoubling(true);
    };
    const doubleDone = (amount) => {
        const { balance, test } = pendingWin;
        setDoubling(false);
        setPendingWin(null);
        clearResultDisplay();
        if (test) {
            if (amount > 0) endTestWin();
            else { setWinnings(0); setMessage("pierde"); setButtonFlag(false); setJogarButtonFlag(false); }
        } else if (amount > 0) {
            playWinningAnimation(balance, amount, 0);
        } else {
            setWinnings(0);
            setMessage("pierde");
            setButtonFlag(false);
            setJogarButtonFlag(false);
        }
    };
    // UD. GANA <-> PRESIONE TOMAR O DOBLAR while the offer stands
    const offering = pendingWin !== null && !doubling;
    useEffect(() => {
        if (!offering) return;
        let k = 0;
        setMessage("take_double");
        const id = setInterval(() => setMessage(++k % 2 ? "win" : "take_double"), 750);
        return () => clearInterval(id);
    }, [offering]);

    // GANA counts down into CRÉDITO, same pacing as Halloween_25. A big win
    // (or any bonus, forceYouWin) is paid behind the YOU WIN screen instead.
    const playWinningAnimation = (currentBalance, pWinnings, accumWin, forceYouWin = false) => {
        const totalWin = pWinnings + accumWin;
        if (totalWin <= 0) return;
        const betCredits = Number(getFormattedAmountWithDenomination(betMultiplier * selectedNumber, denomination)) || 0;
        const bigWin = forceYouWin || (betCredits > 0 && totalWin >= YOU_WIN_MIN_BETS * betCredits);
        let timeFactor = Math.min(Math.max(5000 / totalWin, 2), 20);
        if (timeFactor * totalWin < 2000) timeFactor = Math.ceil(2000 / totalWin);
        const incrementFactor = Math.max(1, Math.floor((totalWin * timeFactor) / 500));
        const pAnimationTimeOutRef = [...animationTimeOutRef];
        // the machine's payout is one short "tik-tik-tik" as GANA starts into
        // CRÉDITO (3VRl 12.4/36.5/79.6/99.4s), no counting loop; behind YOU
        // WIN its own music plays instead
        const playAudio = () => {
            if (!bigWin) playTv(TV_SND.payout);
        };
        const stopAudio = () => { };
        const finalize = () => {
            setWinnings(0);
            setWalletBalance(currentBalance + (totalWin * denomination));
            stopAudio();
            setButtonFlag(false);
            setJogarButtonFlag(false);
            setMessage("press_init");
        };
        const startPhase2 = (startFrom) => {
            playAudio();
            if (bigWin) {
                runYouWin(startFrom, (left, step) => {
                    setWinnings(left);
                    setWalletBalance((prev) => prev + (step * denomination));
                }, finalize, !forceYouWin && hostessWin.current ? "girl" : "guy");
                return;
            }
            let current = startFrom;
            const interval = setInterval(() => {
                if (current <= 0) {
                    clearInterval(interval);
                    finalize();
                    return;
                }
                const step = Math.min(incrementFactor, current);
                current -= step;
                setWinnings(current);
                setWalletBalance((prev) => prev + (step * denomination));
            }, timeFactor);
            setAnimationIntervalRef([interval]);
        };
        const startPhase1 = () => {
            playAudio();
            let current = pWinnings;
            setWinnings(current);
            const interval = setInterval(() => {
                current += incrementFactor;
                if (current >= totalWin) {
                    clearInterval(interval);
                    setWinnings(totalWin);
                    stopAudio();
                    setTimeout(() => startPhase2(totalWin), 2000);
                } else {
                    setWinnings(current);
                }
            }, timeFactor);
            setAnimationIntervalRef([interval]);
        };
        setButtonFlag(true);
        setJogarButtonFlag(true);
        pAnimationTimeOutRef.push(setTimeout(accumWin > 0 ? startPhase1 : () => startPhase2(pWinnings), 1700));
        setAnimationTimeOutRef(pAnimationTimeOutRef);
    };

    const onBonusRoundCompleted = (pTotalBonus) => {
        setShowBonusModel(false);
        setMessage("press_init");
        if (pTotalBonus > 0) {
            // TOMAR while the bonus counts into CRÉDITO, as on the machine
            setMessage("take");
            setWinnings(pTotalBonus);
            if (accumuladoWin.winAmount > 0) {
                setShowCoins(true);
                setTimeout(() => {
                    setShowCoins(false);
                    animateAccumuladoWinning(accumuladoWin.winAmount, accumuladoWin.winIndex);
                    playWinningAnimation(walletBalance, pTotalBonus, accumuladoWin.winAmount, true);
                }, 25000);
            } else {
                playWinningAnimation(walletBalance, pTotalBonus, accumuladoWin.winAmount, true);
            }
            if (autoBetOn) {
                const ref = setTimeout(() => {
                    setAutoCount((prev) => (prev > 0 ? prev - 1 : prev === 0 ? 0 : -1));
                    setPlayMachine((p) => p + 1);
                }, accumuladoWin.winAmount > 0 ? 38000 : 5000);
                setAutoBetTimeOutRef(ref);
            }
        } else {
            setButtonFlag(false);
            setJogarButtonFlag(false);
        }
    };

    // ---- buttons ----
    const cycleLines = () => {
        playTv(TV_SND.lines);
        clearResultDisplay();
        const next = linesFromStart
            ? LINE_OPTIONS[0]
            : LINE_OPTIONS[(LINE_OPTIONS.indexOf(selectedNumber) + 1) % LINE_OPTIONS.length];
        setLinesFromStart(false);
        setSelectedNumber(next);
        setMessage("line_or_bet");
        showLinePreview();
    };

    const increaseBetMultiplier = () => {
        if (!templateData) return;
        playTv(TV_SND.bet);
        clearResultDisplay();
        setMessage("line_or_bet");
        showLinePreview();
        setBetMultiplier((prev) => {
            const list = templateData.betAmountList;
            const i = list.findIndex((b) => b === prev);
            return i === -1 ? list[0] : list[(i + 1) % list.length];
        });
    };

    const stopAuto = () => {
        playSoundById("ButtonClickMp3");
        setAutoBetOn(false);
        setAutoCount(0);
        if (autoBetTimeOutRef) clearTimeout(autoBetTimeOutRef);
    };

    const onIniciar = () => {
        setAutoBetOn(false);
        setAutoCount(0);
        if (slotRunning) {
            stopAllAnimation();
        } else {
            placeBet();
        }
    };

    const openHelp = () => {
        playSoundById("ButtonClickMp3");
        setHelpPage(0);
    };

    const nextHelpPage = () => {
        playSoundById("ButtonClickMp3");
        setHelpPage((p) => (p + 1 >= HELP_PAGES.length ? -1 : p + 1));
    };

    const addBalance = () => {
        callPostApi(ADD_BALANCE_AMT_POST, {}, {}, (resp) => {
            if (resp.errorCode === 0) {
                playTv(TV_SND.coinIn);
                setWalletBalance(resp.responsePacket);
            }
        });
    };

    const resetBalance = () => {
        callPostApi(RESET_BALANCE_AMT_POST, {}, {}, (resp) => {
            if (resp.errorCode === 0) setWalletBalance(resp.responsePacket);
        });
    };

    // same keyboard as Halloween_25 (t help, y/j/h/m/n lines, g bet, p add,
    // r reset, b play/stop; no auto play)
    useEffect(() => {
        const handleKeyPress = (event) => {
            const key = event.key.toLowerCase();
            if (youWin !== null) {
                if (key === "b") skipYouWin();
                return;
            }
            if (doubling) return; // DoubleUp has its own keys
            if (pendingWin) {
                if (key === "b") takeWin();
                else if (key === "d") startDouble();
                return;
            }
            const disabled = jogarButtonFlag || buttonFlag;
            const lineKeys = { y: 3, j: 5, h: 10, m: 15, n: 25 };
            if (key === "t") {
                if (helpPage >= 0) nextHelpPage();
                else if (!disabled) openHelp();
            } else if (lineKeys[key] && !slotRunning && !disabled) {
                playTv(TV_SND.lines);
                clearResultDisplay();
                setLinesFromStart(false);
                setSelectedNumber(lineKeys[key]);
                showLinePreview();
            } else if (key === "g" && !disabled) {
                increaseBetMultiplier();
            } else if (key === "p" && !disabled) {
                addBalance();
            } else if (key === "r" && !disabled && walletBalance > 0) {
                if (!resetAmntModel) {
                    setResetAmntModel(true);
                } else {
                    setResetAmntModel(false);
                    resetBalance();
                }
            } else if (key === "b" && !jogarButtonFlag) {
                onIniciar();
            }
        };
        if (!showBonusModel && keyboardInput) {
            window.addEventListener("keydown", handleKeyPress);
            return () => window.removeEventListener("keydown", handleKeyPress);
        }
    }, [jogarButtonFlag, showBonusModel, walletBalance, selectedNumber, betMultiplier, resetAmntModel, buttonFlag, autoBetOn, slotRunning, helpPage, keyboardInput, templateData, gameResult, youWin, pendingWin, doubling]);

    // ---- rendering ----
    const isWinCell = (row, col) => winCells.some((c) => c.x === row && c.y === col);

    const renderCell = (row, col) => {
        const left = col * CELL_W;
        const top = row * CELL_H;
        const spinning = animateColumn && animateColumn[col];
        if (spinning) {
            // each reel sits at its own point on the strip; the cell below
            // shows the strip one cell further down
            const y = (((col * 290 + row * CELL_H - tick * STRIP_STEP) % STRIP_H) + STRIP_H) % STRIP_H;
            return (
                <div key={`${row}-${col}`} className="tvm-cell" style={{ left, top }}>
                    <div className="tvm-strip" style={{ backgroundImage: `url(${STRIP})`, backgroundPositionY: -y }} />
                </div>
            );
        }
        let src;
        if (gameResult?.resultMatrix) {
            const fig = figOf(gameResult.resultMatrix[row]?.[col]);
            if (winning) {
                src = isWinCell(row, col) ? figFrame(fig, tick) : slotImg(fig, 1);
            } else {
                src = slotImg(fig);
            }
        } else {
            src = slotImg(idleGrid[row][col]);
        }
        return (
            <div key={`${row}-${col}`} className="tvm-cell" style={{ left, top }}>
                <img src={src} alt="" draggable={false} />
            </div>
        );
    };

    const winningLineNums = useMemo(() => new Set(winLines), [winLines]);

    // in play -> coloured star, not in play -> dim purple tile, won -> white star
    const badgeState = (n) => {
        if (winningLineNums.has(n)) return "W";
        return n <= selectedNumber ? "ON" : "D";
    };

    const renderBadges = (list) => {
        return list.map((n) => {
            const st = badgeState(n);
            const b = badgePos(n, st);
            return (
                <img key={n} className={`tvm-abs ${st === "W" ? "tvm-blink" : ""}`} alt=""
                    src={`${TV}/lines/L${pad2(n)}${st}.png`}
                    style={{ left: b.x, top: b.top, width: BADGE_W, height: b.h }} />
            );
        });
    };

    const renderLines = () => {
        if (!linePreview || slotRunning || LINE_SPRITE[selectedNumber] === undefined) return null;
        return <img className="tvm-abs" alt="" src={`${TV}/lines/refactored/sprite_${LINE_SPRITE[selectedNumber]}.png`}
            style={{ left: 42, top: 106, width: 716, height: 371 }} />;
    };

    const MESSAGES = {
        press_init: "msg_press_init",
        line_or_bet: "msg_line_or_bet",
        good_lucky: "msg_good_lucky",
        win: "msg_win",
        pierde: "msg_pierde",
        insert: "msg_insert",
        bonus: "msg_bonus",
        congrats: "msg_congrats",
        juego_jack: "msg_juego_jack",
        take: "msg_take",
        take_double: "msg_take_double",
        double: "msg_double",
        major_minor: "msg_major_minor",
    };

    const busy = jogarButtonFlag || buttonFlag;
    const totalBet = betMultiplier ? getFormattedAmountWithDenomination(betMultiplier * selectedNumber, denomination) : 0;
    const credits = getFormattedAmountWithDenomination(walletBalance, denomination);
    const accValue = (i) => accumuladoList[i]?.accumuladoActual || 0;

    const hasBonus = showBonusModel && gameResult !== null
        && !isEmpty(gameResult.bonusType) && gameResult.bonusType !== "None";
    const showLuckyWheel = hasBonus && WHEEL_BONUS_TYPES.includes(gameResult.bonusType);
    const showJackpotGame = hasBonus && !showLuckyWheel;
    // DEV ONLY: "w" (or OPEN_WHEEL_ON_LOAD) plays a LUCKY WHEEL with a made-up pot
    const [testWheel, setTestWheel] = useState(null);

    // DEV ONLY: "k" plays a JACKPOT GAME round on its own, with a made-up
    // payout and no server call, so the bonus can be looked at without
    // waiting for the reels to deal one. Nothing is paid; it just closes.
    // and with OPEN_BONUS_ON_LOAD it plays once as soon as the game opens
    const [testBonus, setTestBonus] = useState(
        process.env.NODE_ENV === "development" && OPEN_BONUS_ON_LOAD ? 41000 : null);
    // DEV ONLY: the YOU WIN screen on its own, shown only (CRÉDITO untouched)
    const showTestYouWin = (variant = "guy") => {
        setButtonFlag(true);
        setJogarButtonFlag(true);
        runYouWin(41000, (left) => setWinnings(left), () => {
            setWinnings(0);
            setButtonFlag(false);
            setJogarButtonFlag(false);
            setMessage("press_init");
        }, variant);
    };
    // OPEN_DOUBLE_ON_LOAD / OPEN_YOU_WIN_ON_LOAD: once, a moment after the game opens (its frames
    // are preloading meanwhile)
    useEffect(() => {
        if (process.env.NODE_ENV !== "development") return;
        let id;
        if (OPEN_WHEEL_ON_LOAD && !OPEN_BONUS_ON_LOAD) id = setTimeout(() => { setButtonFlag(true); setJogarButtonFlag(true); setTestWheel(60000); }, 1000);
        else if (OPEN_DOUBLE_ON_LOAD && !OPEN_BONUS_ON_LOAD) id = setTimeout(showTestDouble, 1000);
        else if (OPEN_YOU_WIN_ON_LOAD && !OPEN_BONUS_ON_LOAD) id = setTimeout(() => showTestYouWin(YOU_WIN_ON_LOAD_VARIANT), 1000);
        return () => clearTimeout(id);
    }, []);
    useEffect(() => {
        if (process.env.NODE_ENV !== "development") return;
        const onKey = (e) => {
            const key = e.key.toLowerCase();
            if (busy || slotRunning || showBonusModel || testBonus !== null || testWheel !== null || youWin !== null) return;
            if (key === "k") setTestBonus(41000);
            else if (key === "w") { setButtonFlag(true); setJogarButtonFlag(true); setTestWheel(60000); }
            else if (key === "y") showTestYouWin("guy");
            else if (key === "g") showTestYouWin("girl");
            else if (key === "o" && !pendingWin) showTestDouble();
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [busy, slotRunning, showBonusModel, testBonus, testWheel, youWin]);

    const audioFiles = [
        // the reel roll, cut from the machine itself (YouTube 3VRlTIPI0To, 17.94s):
        // the same 1.74s ratchet plays on every spin there, and spin.ogg /
        // stopspin.ogg are not in it (mel NCC 0.40 / 0.58, vs 0.93-0.98 for this)
        { id: "SlotMp3", src: `${process.env.PUBLIC_URL}/tv-milionario/sound/spin-roll.mp3` },
        { id: "ButtonClickMp3", src: `${SFX}/standardClick.mp3` },
        { id: "ErrorMp3", src: `${SFX}/error.mp3` },
    ];

    const formatAmount = (amount) => {
        try {
            return new Intl.NumberFormat("en-US", { style: "currency", currency, minimumFractionDigits: 2 }).format(amount);
        } catch (e) {
            return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 2 }).format(amount);
        }
    };

    return (
        <>
            {showCoins && <CoinContainer />}
            <div className="tvm-viewport">
                <div className="tvm-stage" style={{ transform: `scale(${scale.x}, ${scale.y}) translate(-50%, -50%)` }}>
                    {/* reels */}
                    <div className="tvm-reels">
                        {[0, 1, 2].map((row) => [0, 1, 2, 3, 4].map((col) => renderCell(row, col)))}
                    </div>
                    {/* row bars under the column bars: MAINV already carries the
                        bright joint where they cross */}
                    {[1, 2].map((k) => (
                        <img key={`h${k}`} className="tvm-abs" alt="" src={`${TV}/header/MAINH.png`}
                            style={{ left: REEL_X, top: REEL_Y + k * CELL_H - 2.5, width: 680, height: 5 }} />
                    ))}
                    {[1, 2, 3, 4].map((k) => (
                        <img key={k} className="tvm-divider" alt="" src={`${TV}/header/MAINV.png`}
                            style={{ left: REEL_X + k * CELL_W - 2.5 }} />
                    ))}

                    {/* machine frame */}
                    <img className="tvm-abs" alt="" src={`${TV}/header/MAINT.png`} style={{ left: 0, top: 0, width: 800, height: 109 }} />
                    <MainLogo />
                    <img className="tvm-abs" alt="" src={`${TV}/header/MAINE.png`} style={{ left: 0, top: 109, width: 60, height: 364 }} />
                    <img className="tvm-abs" alt="" src={`${TV}/header/MAIND.png`} style={{ left: 740, top: 109, width: 60, height: 364 }} />
                    <img className="tvm-abs" alt="" src={`${TV}/header/MAINB2.png`} style={{ left: 0, top: 473, width: 800, height: 16 }} />
                    {BULBS.map((b, i) => (
                        <img key={`led${i}`} className="tvm-abs tvm-led" alt="" src={`${TV}/header/MA_LED.png`}
                            style={{ left: b.x - 6.5, top: b.y - 6.5, animationDelay: `${b.delay}s` }} />
                    ))}

                    {renderLines()}
                    {renderBadges(LEFT_BADGES)}
                    {renderBadges(RIGHT_BADGES)}

                    {/* header read-outs */}
                    <div className="tvm-header-val right" style={{ left: 136, top: 43, width: 34 }}>
                        <Digits value={templateData?.betAmountList?.[0] || 0} scale={0.7} />
                    </div>
                    <div className="tvm-header-val right" style={{ left: 40, top: 64, width: 38 }}>
                        <Digits value={betMultiplier || 0} scale={0.7} />
                    </div>
                    <div className="tvm-header-val right" style={{ left: 136, top: 64, width: 34 }}>
                        <Digits value={selectedNumber} scale={0.7} />
                    </div>
                    {[0, 1, 2].map((i) => (
                        <div key={i} className="tvm-header-val right" style={{ left: 655, top: 15 + i * 22, width: 100 }}>
                            <Digits value={accValue(i)} scale={0.8} />
                        </div>
                    ))}

                    {/* footer */}
                    <img className="tvm-abs" alt="" src={`${TV}/footer-buttons/panel.png`} style={{ left: 22, top: 492, width: 756, height: 108 }} />
                    <div className="tvm-header-val" style={{ left: 160, top: 498 }}>
                        <Digits value={credits} font="white" scale={0.9} />
                    </div>
                    {/* AUMENTO and GANA: both blue in TvMillionarioGame.MOV, right
                        edges at x 742.6, 12.9px a digit, centred on y 507 / 527
                        (measured off the video against panel.png's labels) */}
                    <div className="tvm-header-val right" style={{ left: 643, top: 499, width: 100 }}>
                        <Digits value={totalBet} font="blue" scale={0.92} />
                    </div>
                    <div className="tvm-header-val right" style={{ left: 643, top: 519, width: 100 }}>
                        <Digits value={winnings} font="blue" scale={0.92} />
                    </div>
                    <div className="tvm-msg" style={{ left: 72 }}>
                        <img alt="" src={`${TV}/footer-buttons/${MESSAGES[message] || MESSAGES.press_init}.png`}
                            className={message === "win" || message === "insert" ? "tvm-blink" : ""} />
                    </div>

                    <TvButton name="bt_menu" x={23} onClick={() => navigate("/")} disabled={busy} />
                    {/* blank key: does nothing */}
                    {offering
                        ? <TvButton name="bt_db" x={132} onClick={startDouble} />
                        : <TvButton name="bt_none" x={132} disabled={busy} />}
                    <TvButton name="bt_help" x={241} onClick={openHelp} disabled={busy} />
                    <TvButton name="bt_lines" x={350} onClick={cycleLines} disabled={busy} />
                    <TvButton name="bt_bet" x={459} onClick={increaseBetMultiplier} disabled={busy} />
                    <TvButton name="bt_quick" x={568} onClick={stopAllAnimation} disabled={!slotRunning || jogarButtonFlag} />
                    {autoBetOn
                        ? <TvButton name="bt_stop" x={678} onClick={stopAuto} />
                        : youWin !== null
                            ? <TvButton name="bt_take" x={678} onClick={skipYouWin} />
                            : offering
                                ? <TvButton name="bt_take" x={678} onClick={takeWin} />
                                : <TvButton name="bt_init" x={678} onClick={onIniciar} disabled={jogarButtonFlag} lit={slotRunning} />}

                    {youWin !== null && <YouWin value={youWin} onSkip={skipYouWin} variant={youWinVariant} />}
                    {doubling && pendingWin && (
                        <DoubleUp amount={pendingWin.amount} Button={TvButton} keyboardInput={keyboardInput}
                            onAmount={setWinnings} onMessage={setMessage} onDone={doubleDone} />
                    )}

                    {autoBetOn && <div className="tvm-auto-flag">JUEGO AUTOMATICO</div>}

                    {showInsufficient && <div className="tvm-popup">INSERTAR FICHA(S)</div>}
                    {resetAmntModel && (
                        <div className="tvm-popup">
                            <div>N.SEC. 001</div>
                            <div style={{ margin: "16px 0" }}>
                                <div>FAVOR CHAMAR 0 OPERADOR</div>
                                <div>PARA RECEBER SEUS CREDITOS</div>
                                <div style={{ marginTop: 10 }}>CREDITOS: {credits}</div>
                                <div>{formatAmount(walletBalance)}</div>
                            </div>
                            <div>VALIDACAO : 538978871</div>
                        </div>
                    )}

                    {showJackpotGame && (
                        <JackpotGameBonus
                            gameResult={gameResult}
                            gameSessionRecordId={gameResult.recordId}
                            keyboardInput={keyboardInput}
                            credits={credits}
                            totalBet={totalBet}
                            accumulados={[0, 1, 2].map(accValue)}
                            onBonusRoundCompleted={onBonusRoundCompleted} />
                    )}
                    {showLuckyWheel && (
                        <LuckyWheelBonus
                            gameResult={gameResult}
                            gameSessionRecordId={gameResult.recordId}
                            keyboardInput={keyboardInput}
                            lineWin={gameResult.winnings > 0 ? Number(gameResult.winnings) : 0}
                            Button={TvButton}
                            onGana={setWinnings}
                            onMessage={setMessage}
                            onDone={onBonusRoundCompleted} />
                    )}
                    {testWheel !== null && !hasBonus && (
                        <LuckyWheelBonus
                            gameResult={{ bonusType: "Test" }}
                            keyboardInput
                            testPot={testWheel}
                            Button={TvButton}
                            onGana={setWinnings}
                            onMessage={setMessage}
                            onDone={(amount) => {
                                setTestWheel(null);
                                // the YOU WIN that follows, shown only: CRÉDITO is left alone
                                runYouWin(amount, (left) => setWinnings(left), () => {
                                    setWinnings(0);
                                    setButtonFlag(false);
                                    setJogarButtonFlag(false);
                                    setMessage("press_init");
                                });
                            }} />
                    )}
                    {testBonus !== null && !showJackpotGame && (
                        <JackpotGameBonus
                            gameResult={{ bonusType: "Test" }}
                            keyboardInput
                            credits={credits}
                            totalBet={totalBet}
                            accumulados={[0, 1, 2].map(accValue)}
                            testPayout={testBonus}
                            onBonusRoundCompleted={(amount) => {
                                setTestBonus(null);
                                // the YOU WIN that follows, shown only: GANA
                                // counts but CRÉDITO is left alone
                                setButtonFlag(true);
                                setJogarButtonFlag(true);
                                runYouWin(amount, (left) => setWinnings(left), () => {
                                    setWinnings(0);
                                    setButtonFlag(false);
                                    setJogarButtonFlag(false);
                                    setMessage("press_init");
                                });
                            }} />
                    )}

                    {helpPage >= 0 && (
                        <div className="tvm-help" onClick={nextHelpPage}>
                            <img src={HELP_PAGES[helpPage]} alt="" draggable={false} />
                            <div className="tvm-help-bar" onClick={(e) => e.stopPropagation()}>
                                <TvButton name="bt_menu" onClick={() => setHelpPage(-1)} />
                                <TvButton name="bt_skip" onClick={nextHelpPage} />
                            </div>
                        </div>
                    )}
                </div>
            </div>

            <div style={{ display: "none" }}>
                {audioFiles.map((a) => (
                    <audio key={a.id} id={a.id} className="audioFile" preload="auto">
                        <source src={a.src} type={a.src.endsWith(".ogg") ? "audio/ogg" : "audio/mpeg"} />
                    </audio>
                ))}
            </div>
        </>
    );
};

export default TvMillionarioGame;
